import React from 'react';
import { cn } from '../../utils/cn';

export interface ProgressBarProps {
  currentPoints: number;
  maxPoints: number;
  variant?: 'red' | 'green';
  className?: string;
  label?: string;
  showPercentage?: boolean;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  currentPoints,
  maxPoints,
  variant = 'red',
  className,
  label,
  showPercentage = false,
}) => {
  const progressPercentage = maxPoints > 0 ? Math.min((currentPoints / maxPoints) * 100, 100) : 0;

  const styles = {
    red: {
      background: 'bg-velux-background-red',
      fill: 'bg-velux-red',
      height: 'h-1',
    },
    green: {
      background: 'bg-velux-background-grey',
      fill: 'bg-velux-splash-green',
      height: 'h-2',
    },
  };

  const { background, fill, height } = styles[variant];

  return (
    <div className={cn('w-full', className)}>
      {(label || showPercentage) && (
        <div className="flex justify-between mb-2">
          {label && <span className="text-sm text-velux-grey">{label}</span>}
          {showPercentage && (
            <span className="text-sm text-velux-grey">{Math.round(progressPercentage)}%</span>
          )}
        </div>
      )}
      <div
        className={cn('w-full flex justify-between overflow-hidden', height, background)}
        role="progressbar"
        aria-valuenow={currentPoints}
        aria-valuemin={0}
        aria-valuemax={maxPoints}
        aria-label={label || 'Progress'}
      >
        <div
          data-testid="progress-bar-active"
          className={cn('h-full transition-all duration-300', fill)}
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
    </div>
  );
};

ProgressBar.displayName = 'ProgressBar';

export { ProgressBar };
